import type { Store } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import {
  CUSTOMER_QUOTE_CANCEL_REQUEST,
  CustomerQuote,
  NotificationVariants,
  POST,
  formattedDateForSubscription,
  logger,
  request,
  type DataResponse,
  type ErrorSubscriptionResponse,
  type IWebSocketClient,
  type ToastProps,
} from '@talos/kyoko';
import { v4 as uuid } from 'uuid';
import { getRFQPricingActions } from './ManualRFQPricingSlice';
import { buildCustomerQuotePricingRequest } from './pricingRequest';
import type { ManualRFQPricingFormState, ManualRFQPricingState } from './types';

const PUBLICATION_CLEANUP_TIMEOUT = 10_000; // wait 10s for any error to come back before we don't care any more. Pretty arbitrary, good enough

export class ManualRFQPricingService {
  constructor(
    private store: Store<AppState>,
    private instanceID: string,
    private webSocketClient: IWebSocketClient<unknown>,
    private orgApiEndpoint: string,
    private addToast: (props: ToastProps) => void
  ) {}

  updateQuote() {
    const quote = this.state.currentCustomerQuote;
    if (quote == null) {
      throw new Error('No quote selected');
    }

    let body;
    try {
      body = buildCustomerQuotePricingRequest({ quote, formState: this.formState });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Invalid pricing request';
      this.addToast({
        text: `Failed to price quote: ${message}`,
        variant: NotificationVariants.Negative,
        dismissable: true,
        timeout: 5000,
      });
      return;
    }

    this.store.dispatch(getRFQPricingActions(this.instanceID).setIsLoading(true));

    request<DataResponse<CustomerQuote>>(
      POST,
      `${this.orgApiEndpoint}/customer/quotes/${quote.RFQID}/submit-price`,
      body
    )
      .then(response => {
        const responseEntity = response.data.at(0);
        if (!responseEntity) {
          this.addToast({
            text: `Invalid quote response`,
            variant: NotificationVariants.Negative,
            dismissable: true,
            timeout: 3000,
          });
          return;
        }

        const quote = new CustomerQuote(responseEntity);
        if (quote) {
          this.store.dispatch(getRFQPricingActions(this.instanceID).setCurrentCustomerQuote(quote));
        }

        this.addToast({
          text: `Quote price submitted successfully.`,
          variant: NotificationVariants.Positive,
          dismissable: true,
          timeout: 3000,
        });
      })
      .catch((e: ErrorEvent | Error) => {
        this.addToast({
          text: `Failed to price quote: ${e.message}`,
          variant: NotificationVariants.Negative,
          dismissable: true,
          timeout: 5000,
        });
      })
      .finally(() => {
        this.store.dispatch(getRFQPricingActions(this.instanceID).setIsLoading(false));
      });
  }

  public cancelQuote(): void {
    const quoteReqId = uuid();
    const currentQuote = this.state.currentCustomerQuote;
    if (currentQuote == null) {
      throw new Error('No quote selected');
    }

    this.store.dispatch(getRFQPricingActions(this.instanceID).setIsLoading(true));

    const request = {
      RFQID: currentQuote.RFQID,
      QuoteReqID: quoteReqId,
      Counterparty: currentQuote.Counterparty,
      TransactTime: formattedDateForSubscription(new Date()),
      PricingMode: currentQuote.PricingMode,
    };

    const cleanup = this.webSocketClient.registerPublication(
      {
        type: CUSTOMER_QUOTE_CANCEL_REQUEST,
        data: [request],
      },
      error => this.handleQuoteCancelError(error)
    );

    setTimeout(() => cleanup(), PUBLICATION_CLEANUP_TIMEOUT);
  }

  private handleQuoteCancelError(error: ErrorSubscriptionResponse) {
    this.store.dispatch(getRFQPricingActions(this.instanceID).setIsLoading(false));
    logger.error(new Error('Manual RFQ Pricing Service - cancel quote failed'), { extra: error });
    this.addToast({
      text: `Error: ${error.error?.msg ?? 'Failed to cancel quote'}`,
      variant: NotificationVariants.Negative,
      timeout: 5000,
      dismissable: true,
    });
  }

  private get state(): ManualRFQPricingState {
    return this.store.getState().manualRFQPricing[this.instanceID];
  }

  private get formState(): ManualRFQPricingFormState {
    return this.state.form;
  }

  public destroy(): void {}
}
